// src/features/services/ServicesAdmin.jsx
import React, { useEffect, useState } from "react";
import { apiGet, apiDel, apiPostForm, apiPutForm } from "../../admin/api";
import Editor from "../../admin/components/Editor";
import ServicesCard from "./ServicesCard";
import "./services-admin.css";

export default function ServicesAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    id: null,
    title: "",
    excerpt: "",
    status: "published",
    content_html: ""
  });
  const [cover, setCover] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet("/api/services?status=&page=1&limit=100");
      setRows(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setForm({ id: null, title: "", excerpt: "", status: "published", content_html: "" });
    setCover(null);
    setShowForm(true);
  };

  const startEdit = (r) => {
    setForm({
      id: r.id,
      title: r.title,
      excerpt: r.excerpt || "",
      status: r.status || "published",
      content_html: r.content_html || ""
    });
    setCover(null);
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title,
      excerpt: form.excerpt,
      status: form.status,
      content_html: form.content_html
    };
    if (cover) payload.cover_image = cover;

    if (form.id) {
      await apiPutForm(`/api/services/${form.id}`, payload);
    } else {
      await apiPostForm(`/api/services`, payload);
    }
    setShowForm(false);
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await apiDel(`/api/services/${id}`);
    await load();
  };

  return (
    <div className="services-admin">
      {/* Toolbar */}
      <div className="services-toolbar">
        <h2 className="services-toolbar__title">Services</h2>
        <button className="services-btn services-btn--cta" onClick={startNew}>
          + New
        </button>
      </div>

      {/* Grid / Loading / Empty */}
      {loading ? (
        <div className="services-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="services-empty-block">
          <div className="services-empty-block__icon">🧰</div>
          <div className="services-empty-block__title">No services yet</div>
          <div className="services-empty-block__desc">Click “New” to add your first service.</div>
          <button className="services-btn services-btn--cta" onClick={startNew}>
            + Create service
          </button>
        </div>
      ) : (
        <div className="services-grid">
          {rows.map((r) => (
            <ServicesCard key={r.id} item={r} onEdit={startEdit} onDelete={remove} />
          ))}
        </div>
      )}

      {/* Top overlay form */}
      {showForm && (
        <div className="services-modal" role="dialog" aria-modal="true">
          <div className="services-modal__backdrop" onClick={() => setShowForm(false)} />
          <div className="services-modal__card" role="document">
            <div className="services-modal__head">
              <h3 className="services-modal__title">
                {form.id ? "Edit Service" : "New Service"}
              </h3>
              <button
                className="services-btn services-btn--close"
                onClick={() => setShowForm(false)}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={save} className="services-form">
              <label className="services-field">
                <span className="services-field__label">Title</span>
                <input
                  className="services-field__input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  placeholder="Enter title"
                />
              </label>

              <label className="services-field">
                <span className="services-field__label">Excerpt</span>
                <input
                  className="services-field__input"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  placeholder="Short summary…"
                />
              </label>

              <div className="services-row">
                <label className="services-field services-grow">
                  <span className="services-field__label">Status</span>
                  <select
                    className="services-field__input"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="published">published</option>
                    <option value="draft">draft</option>
                  </select>
                </label>

                <label className="services-field services-grow">
                  <span className="services-field__label">Cover Image</span>
                  <input
                    className="services-field__input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setCover(e.target.files?.[0] || null)}
                  />
                </label>
              </div>

              <label className="services-field">
                <span className="services-field__label">Content (Jodit HTML)</span>
                <div className="services-editor">
                  <Editor
                    value={form.content_html}
                    onChange={(html) => setForm({ ...form, content_html: html })}
                  />
                </div>
              </label>

              <div className="services-modal__actions">
                <button
                  type="button"
                  className="services-btn services-btn--sm"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="services-btn services-btn--primary">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
